// Owns the page's single SSE connection across the back/forward cache.
//
// A page entering the bfcache keeps its EventSource open. Over HTTP/1.1 every
// open stream pins one of the browser's 6 connections to the host, so after a
// few full-page navigations the cached pages hold every slot and the next
// click stalls in the browser's queue (observed: 30-60s) until one of them is
// evicted. The connection is therefore closed on pagehide and reopened on a
// bfcache restore (pageshow with persisted=true).

export interface ClosableSource {
    close(): void;
}

export class SSELifecycle<S extends ClosableSource> {
    private source: S | null = null;
    private connecting = false;
    // Bumped on every stop(). A connect still awaiting its JWT when the page is
    // hidden would otherwise resolve after the restore and install a stream
    // nothing closes - the exact leak this class exists to prevent.
    private generation = 0;
    private urls: [string, string] | null = null;

    constructor(
        private readonly open: (sseURL: string, tokenURL: string) => Promise<S | null>,
        private readonly onConnected: (source: S) => void,
        private readonly onClosed: () => void,
    ) {}

    start(sseURL: string, tokenURL: string): void {
        this.urls = [sseURL, tokenURL];
        if (this.source || this.connecting) return;

        this.connecting = true;
        const gen = this.generation;
        this.open(sseURL, tokenURL).then(
            (source) => {
                if (gen !== this.generation) {
                    source?.close();
                    return;
                }
                this.connecting = false;
                if (!source) return;
                this.source = source;
                this.onConnected(source);
            },
            () => {
                if (gen === this.generation) this.connecting = false;
            },
        );
    }

    stop(): void {
        this.generation++;
        this.connecting = false;
        if (!this.source) return;
        this.source.close();
        this.source = null;
        this.onClosed();
    }

    // Reopens with the URLs of the last start(); a no-op before the first one.
    restart(): void {
        if (this.urls) this.start(...this.urls);
    }

    bind(target: EventTarget): void {
        target.addEventListener("pagehide", () => this.stop());
        target.addEventListener("pageshow", (e) => {
            if ((e as PageTransitionEvent).persisted) this.restart();
        });
    }
}
